// Окна: «Как работает Правосвод», вход, образец документа, цитата из загруженного документа.
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { getJson, postJson } from "../api";
import { boot, type Paragraph } from "../boot";
import { loginStore, Store, useStore, type SourceRef } from "../store";
import { DocPage } from "./DocPage";
import { Icon, ProviderLogo } from "./Icon";

// Окно <dialog>: открывается, когда меняется open; щелчок мимо окна закрывает его.
// Окно открывается новым нажатием (счётчик open вырос), а не тем, что раздел нарисовали заново:
// после «Назад» чат появляется снова, и прежнее открытие цитаты или образца не должно повторяться.
function useDialog(open: number | boolean): RefObject<HTMLDialogElement | null> {
  const ref = useRef<HTMLDialogElement>(null);
  const shown = useRef(open);
  useEffect(() => {
    const dialog = ref.current;
    if (open === shown.current) return;
    shown.current = open;
    if (open && dialog && !dialog.open) dialog.showModal();
  }, [open]);
  return ref;
}

const closeOnBackdrop = (event: React.MouseEvent<HTMLDialogElement>) => {
  if (event.target === event.currentTarget) event.currentTarget.close();
};

function CloseButton() {
  return <form method="dialog" className="dialog-close"><button className="icon-button" aria-label="Закрыть"><Icon name="close" /></button></form>;
}

export const helpStore = new Store(0);

export function HelpDialog() {
  const open = useStore(helpStore);
  const ref = useDialog(open);
  return (
    <dialog ref={ref} className="login-dialog help-dialog" id="help-dialog" aria-labelledby="help-title" onClick={closeOnBackdrop}>
      <CloseButton />
      <span className="action-icon"><Icon name="scales" /></span>
      <h2 id="help-title">Правосвод под рукой</h2>
      <p>Выберите действие на главной или напишите, что нужно сделать.</p>
      <ul>
        <li><strong>Госпошлина.</strong> Пошаговый расчёт с формулой и ссылкой на норму.</li>
        <li><strong>Документы.</strong> Ответьте на вопросы и скачайте готовый Word.</li>
        <li><strong>Разбор документа.</strong> Прикрепите файл, получите выжимку и задайте вопросы по тексту. Цитаты открываются в отдельном окне, а переписка сохраняется в «Моих документах». Из судебного приказа можно составить возражения.</li>
      </ul>
      <p className="dialog-note">Свободные консультации и поиск судебной практики пока в разработке.</p>
    </dialog>
  );
}

const DEFAULT_REASON = "Документы и расчёты сохранятся в аккаунте — их можно будет открыть с любого устройства.";

// Вход по почте: адрес → письмо с кодом → код. Новый код — не чаще раза в минуту.
function EmailLogin({ shown }: { shown: boolean }) {
  const [step, setStep] = useState<"address" | "code">("address");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (left <= 0) return;
    const timer = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(timer);
  }, [left]);
  useEffect(() => { if (shown) (step === "code" ? codeRef : emailRef).current?.focus(); }, [shown, step]);

  async function sendCode(to: string) {
    setBusy(true);
    try {
      const data = await postJson<{ email: string }>("/auth/email/start", { email: to });
      setEmail(data.email);
      setStep("code");
      setError("");
      setCode("");
      setLeft(60);
      codeRef.current?.focus();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    if (busy) return;
    setBusy(true);
    try {
      const data = await postJson<{ next: string }>("/auth/email/verify", { email, code: value, next: location.pathname });
      location.href = data.next;
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <form className="email-login" id="email-login" noValidate hidden={!shown}
      onSubmit={(event) => { event.preventDefault(); if (step === "address") sendCode(address); else verify(code); }}>
      <div className="email-step" hidden={step !== "address"}>
        <label htmlFor="login-email">Ваша почта</label>
        <input ref={emailRef} id="login-email" name="email" type="email" autoComplete="email" inputMode="email" placeholder="name@mail.ru"
          value={address} onChange={(event) => setAddress(event.target.value)} />
        <button type="submit" className="button" disabled={busy}>Получить код</button>
      </div>
      <div className="email-step" hidden={step !== "code"}>
        <p className="email-sent">{email && `Мы отправили код на ${email}. Письмо приходит в течение минуты — если его нет, загляните в папку «Спам».`}</p>
        <label htmlFor="login-code">Код из письма</label>
        <input ref={codeRef} id="login-code" name="code" autoComplete="one-time-code" inputMode="numeric" maxLength={6} placeholder="6 цифр"
          value={code} onChange={(event) => {
            setCode(event.target.value);
            if (/^\d{6}$/.test(event.target.value.trim())) verify(event.target.value);   // шесть цифр — входим сразу
          }} />
        <button type="submit" className="button" disabled={busy}>Войти</button>
        <p className="dialog-note">
          <button type="button" className="link-button" disabled={left > 0} onClick={() => sendCode(email)}>
            {left > 0 ? `Отправить ещё раз через ${left} с` : "Отправить код ещё раз"}
          </button>{" · "}
          <button type="button" className="link-button" onClick={() => { setStep("address"); setError(""); }}>Изменить почту</button>
        </p>
      </div>
      <p className="error" role="alert" hidden={!error}>{error}</p>
    </form>
  );
}

export function LoginDialog() {
  const { open, reason } = useStore(loginStore);
  const ref = useDialog(open);
  const [emailShown, setEmailShown] = useState(false);
  if (!boot.isGuest) return null;
  const next = encodeURIComponent(location.pathname);
  return (
    <dialog ref={ref} className="login-dialog" id="login-dialog" aria-labelledby="login-title" onClick={closeOnBackdrop}>
      <CloseButton />
      <h2 id="login-title">Войти</h2>
      <p className="dialog-lead">{reason || DEFAULT_REASON}</p>
      <div className="login-options">
        {boot.loginOptions.map((option) => option.enabled ? (
          <a key={option.slug} className="login-button" href={`/auth/${option.slug}?next=${next}`}><ProviderLogo slug={option.slug} />Войти через {option.title}</a>
        ) : (
          <span key={option.slug} className="login-button is-disabled"><ProviderLogo slug={option.slug} />Войти через {option.title}<em>подключается</em></span>
        ))}
        {boot.emailLogin ? (
          <button type="button" className="login-button" onClick={() => setEmailShown(true)}><Icon name="mail" />Войти по почте</button>
        ) : (
          <span className="login-button is-disabled"><Icon name="mail" />Войти по почте<em>подключается</em></span>
        )}
      </div>
      {boot.emailLogin && <EmailLogin shown={emailShown} />}
      <p className="dialog-note">Нет аккаунта? Он создастся при первом входе, а всё, что вы сделали как гость, перейдёт в него.</p>
      <p className="dialog-note">Входя, вы соглашаетесь с <a href="/privacy">политикой конфиденциальности</a>.</p>
    </dialog>
  );
}

export const previewStore = new Store<{ open: number; title: string; preview: Paragraph[] }>({ open: 0, title: "", preview: [] });
export const openPreview = (title: string, preview: Paragraph[]) =>
  previewStore.set({ open: previewStore.get().open + 1, title, preview });

export function PreviewDialog() {
  const { open, title, preview } = useStore(previewStore);
  const ref = useDialog(open);
  return (
    <dialog ref={ref} className="preview-dialog" id="preview-dialog" aria-labelledby="preview-title">
      <div className="preview-head">
        <div>
          <h2 id="preview-title">Образец: {title}</h2>
          <p className="preview-legend"><mark>жёлтым</mark> — что вы укажете в ответах, <mark className="is-auto">голубым</mark> — что заполнится само. Данные в образце — примерные.</p>
        </div>
        <form method="dialog"><button className="icon-button" aria-label="Закрыть"><Icon name="close" /></button></form>
      </div>
      <div className="preview-body"><DocPage paragraphs={preview} titles /></div>
    </dialog>
  );
}

export const sourceStore = new Store<{ open: number; docId: string; ref: SourceRef | null }>({ open: 0, docId: "", ref: null });
export const openSource = (docId: string, ref: SourceRef) => sourceStore.set({ open: sourceStore.get().open + 1, docId, ref });

// Цитата из загруженного документа — в окружении текста, с подсветкой.
export function SourceDialog() {
  const { open, docId, ref: source } = useStore(sourceStore);
  const ref = useDialog(open);
  const [body, setBody] = useState<ReactNode>("Открываю фрагмент…");
  useEffect(() => {
    if (!open || !source) return;
    let current = true;
    setBody("Открываю фрагмент…");
    getJson<{ text: string }>(`/api/documents/${docId}/sources/${source.source_id}`).then((data) => {
      if (!current) return;
      const text = data.text.replace(/\s+/g, " ").trim();
      const at = text.indexOf(source.quote);
      setBody(at < 0 ? text : <>{text.slice(0, at)}<mark>{source.quote}</mark>{text.slice(at + source.quote.length)}</>);
    }).catch((error) => { if (current) setBody((error as Error).message); });
    return () => { current = false; };
  }, [open, docId, source]);
  return (
    <dialog ref={ref} className="source-dialog" id="source-dialog" aria-labelledby="source-title">
      <div className="preview-head">
        <div>
          <h2 id="source-title">{source?.label || "Цитата из документа"}</h2>
          <p className="review-footnote">Распознанный текст. Сверьте с оригиналом, если в скане есть ошибки.</p>
        </div>
        <form method="dialog"><button className="icon-button" aria-label="Закрыть"><Icon name="close" /></button></form>
      </div>
      <div className="source-text">{body}</div>
      <a className="chip" href={`/api/documents/${docId}/file`} download>Скачать оригинал</a>
    </dialog>
  );
}
